import { useState } from "react";
import { X, Cable, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function SlackProposalDialog({ proposal, onClose, onDone }) {
  const { t } = useI18n();
  const { cable, hints } = proposal;
  const [rows, setRows] = useState(hints.map((h) => ({ ...h, checked: true, meters: h.meters })));
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setRows((r) => r.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  const save = async () => {
    const chosen = rows.filter((r) => r.checked && Number(r.meters) > 0);
    if (!chosen.length) { toast.error(t("Zaznacz przynajmniej jeden zapas")); return; }
    setSaving(true);
    try {
      let added = 0;
      for (const r of chosen) {
        await api.post("/cable-slack/add", {
          cable: cable.id, length_m: Number(r.meters), reserve_type: r.reserve_type || "Inny",
          note: `Auto: zawrót${r.host_label ? ` przy ${r.reserve_type} ${r.host_label}` : ""}`,
          lat: r.lat, lng: r.lng, host_type: r.reserve_type === "Mufa" ? "closures" : "poles",
        });
        added += 1;
      }
      toast.success(`${t("Dodano zapasy")}: ${added}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[905] flex items-center justify-center bg-black/50 p-4" data-testid="slack-proposal-dialog">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
          <span className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#F43F5E22" }}>
            <Cable size={16} className="text-rose-400" />
          </span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-100">{t("Wykryto zawroty trasy (możliwe zapasy)")}</div>
            <div className="text-[11px] text-slate-400">{t("Kabel")}: {cable.number || cable.id} · {t("dodać jako zapasy kabla?")}</div>
          </div>
          <button onClick={onClose} data-testid="slack-proposal-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} data-testid={`slack-proposal-row-${i}`}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 ${r.checked ? "border-rose-500/50 bg-rose-500/10" : "border-slate-800"}`}>
              <input type="checkbox" checked={r.checked} onChange={(e) => upd(i, { checked: e.target.checked })}
                data-testid={`slack-proposal-check-${i}`} className="accent-rose-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200">{r.reserve_type || t("Punkt")} {r.host_label || "—"}</div>
                <div className="text-[11px] text-slate-500 font-mono">{r.lat?.toFixed?.(5)}, {r.lng?.toFixed?.(5)}</div>
              </div>
              <input type="number" min="0" step="0.5" value={r.meters} onChange={(e) => upd(i, { meters: e.target.value })}
                data-testid={`slack-proposal-meters-${i}`}
                className="w-20 bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-sm text-slate-100 text-right focus:border-cyan-500 outline-none" />
              <span className="text-xs text-slate-400">m</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-800">
          <button onClick={onClose} data-testid="slack-proposal-skip"
            className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">{t("Pomiń")}</button>
          <button onClick={save} disabled={saving} data-testid="slack-proposal-save"
            className="px-4 py-1.5 text-sm bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white rounded-md flex items-center gap-1.5">
            <Check size={14} /> {saving ? t("Zapisywanie…") : t("Dodaj zaznaczone")}
          </button>
        </div>
      </div>
    </div>
  );
}
